import { WebSocket } from 'partysocket'

import type { MaterializedGrid } from 'honeygrid-types'

export type WebSocketMessage =
	| { type: 'grid'; data: MaterializedGrid }
	| { type: 'needsSeed' }
	| { type: 'error'; message: string }

export type MessageHandler = (message: WebSocketMessage) => void

class WebSocketManager {
	private static instance: WebSocketManager
	private connections: Map<string, WebSocket> = new Map()
	private connectionCounts: Map<string, number> = new Map()
	private messageHandlers: Map<string, Set<MessageHandler>> = new Map()

	private constructor() {}

	static getInstance(): WebSocketManager {
		if (!this.instance) {
			this.instance = new WebSocketManager()
		}
		return this.instance
	}

	private getConnectionKey(workspaceId: string, gridId: string): string {
		return `${workspaceId}/${gridId}`
	}

	async getConnection(workspaceId: string, gridId: string, token: string): Promise<WebSocket> {
		const key = this.getConnectionKey(workspaceId, gridId)
		let connection = this.connections.get(key)

		if (!connection) {
			if (!token) throw new Error('No auth token provided')

			connection = new WebSocket(
				`${import.meta.env.VITE_API_URL?.replace(
					'http',
					'ws'
				)}/ws/workspaces/${workspaceId}/grids/${gridId}/ws?token=${token}`
			)
			console.log('adding connection', connection)
			this.connections.set(key, connection)

			connection.addEventListener('message', (event) => {
				const data = JSON.parse(event.data)
				const handlers = this.messageHandlers.get(key) ?? new Set()
				handlers.forEach((handler) => handler(data))
			})

			this.connectionCounts.set(key, 0)
			this.messageHandlers.set(key, new Set())
		}

		this.connectionCounts.set(key, (this.connectionCounts.get(key) ?? 0) + 1)
		return connection
	}

	addMessageHandler(workspaceId: string, gridId: string, handler: MessageHandler) {
		const key = this.getConnectionKey(workspaceId, gridId)
		const handlers = this.messageHandlers.get(key) ?? new Set()
		handlers.add(handler)
		this.messageHandlers.set(key, handlers)
	}

	removeMessageHandler(workspaceId: string, gridId: string, handler: MessageHandler) {
		const key = this.getConnectionKey(workspaceId, gridId)
		const handlers = this.messageHandlers.get(key)
		if (handlers) {
			handlers.delete(handler)
		}
	}

	releaseConnection(workspaceId: string, gridId: string) {
		const key = this.getConnectionKey(workspaceId, gridId)
		const count = this.connectionCounts.get(key) ?? 0

		if (count <= 1) {
			const connection = this.connections.get(key)
			if (connection) {
				connection.close()
				this.connections.delete(key)
				this.connectionCounts.delete(key)
				this.messageHandlers.delete(key)
			}
		} else {
			this.connectionCounts.set(key, count - 1)
		}
	}

	async sendMessage(workspaceId: string, gridId: string, token: string, message: any) {
		const connection = await this.getConnection(workspaceId, gridId, token)
		console.log('connection', connection)
		if (connection) {
			connection.send(JSON.stringify(message))
		}
	}
}

// Export a singleton instance
export const webSocketManager = WebSocketManager.getInstance()
